import {
  ArrowBack as ArrowBackIcon,
  FileDownload as DownloadIcon,
  KeyboardArrowDown as ExpandedIcon,
  KeyboardArrowRight as CollapsedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { DateUtils } from '../../utils';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import { selectCurrentUser } from '../user/user-slice';
import {
  BOM_PRODUCT_TYPES,
  bomRiskLevels,
  filterBOMRecords,
  fmtRange,
  groupBOMRecords,
  riskLabel,
  windRangeLabel,
  windRanges,
} from './bom-utils';
import { BOMRecordDetail } from './components/bom-record-detail';
import { RiskToggleBar } from './components/risk-toggle-bar';
import { exportBOMSpecificationsCsv, exportBOMSpecificationsPdf } from './export-utils';
import { useGetBOMSpecificationsQuery } from './price-management-api-slice';

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const COLUMNS = [
  'Code',
  'Span Range (ft)',
  'Wind (mph)',
  'Cost of Steel',
  'Hardware Cost',
  'Cost of Paint',
  'Labor Hrs',
  'Labor Cost',
  'Total Cost',
  'MSRP',
];
const LEFT_COLUMNS = ['Code', 'Span Range (ft)', 'Wind (mph)'];

/**
 * BOM Specifications Page (BOM Management v2)
 * Read-only view of a manufacturer's BOM (Bill of Materials) records with costs calculated from the
 * latest published Price Version and Paint Data. BOM data is maintained by SOLTECH Support.
 */
const BOMSpecificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector(selectCurrentUser);

  const manufacturerIdParam = searchParams.get('manufacturerId');
  const manufacturerId = manufacturerIdParam ? parseInt(manufacturerIdParam, 10) : undefined;
  const effectiveManufacturerId = manufacturerId ?? currentUser?.manufacturerId;

  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !effectiveManufacturerId,
  });

  const { data, isLoading, error } = useGetBOMSpecificationsQuery(
    { manufacturerId: effectiveManufacturerId! },
    { skip: !effectiveManufacturerId }
  );

  const [selectedRiskLevel, setSelectedRiskLevel] = useState<string>('I');
  const [productType, setProductType] = useState<string>('All');
  const [windSpeedFrom, setWindSpeedFrom] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const records = useMemo(() => data?.records ?? [], [data]);
  const riskLevels = useMemo(() => bomRiskLevels(records), [records]);
  const ranges = useMemo(() => windRanges(records), [records]);

  const filtered = useMemo(
    () =>
      filterBOMRecords(records, {
        riskLevel: selectedRiskLevel,
        productType,
        windSpeedFrom,
        search,
      }),
    [records, selectedRiskLevel, productType, windSpeedFrom, search]
  );
  const groups = useMemo(() => groupBOMRecords(filtered), [filtered]);

  const manufacturerName =
    manufacturers.find(m => m.id === effectiveManufacturerId)?.licenseeName ??
    currentUser?.manufacturer ??
    data?.manufacturerName ??
    '';

  const toggleExpanded = (id: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (currentUser?.role === UserRole.DEALER) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          BOM Specifications
        </Typography>
        <Alert severity="error" sx={{ mt: 1 }}>
          <strong>Access Restricted</strong> — BOM Specifications are not available for Dealer
          accounts.
        </Alert>
      </Box>
    );
  }

  if (!effectiveManufacturerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          BOM Specifications
        </Typography>
        <Alert severity="warning">
          No manufacturer selected. Please navigate here from the Material Pricing Calculator.
        </Alert>
      </Box>
    );
  }

  const cellSx = { fontSize: '0.8125rem', fontFamily: 'monospace' };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Box sx={{ ml: 1 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              BOM Specifications
              <Chip
                label="READ-ONLY"
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 600 }}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {manufacturerName && `${manufacturerName} · `}Materials and quantities for each
              standard truss. BOM data is maintained by SOLTECH Support; contact Support to request
              changes.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download as CSV">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!data || filtered.length === 0}
                onClick={() =>
                  exportBOMSpecificationsCsv(filtered, manufacturerName, data!.generatedAt)
                }
              >
                CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download as PDF">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!data || filtered.length === 0}
                onClick={() =>
                  exportBOMSpecificationsPdf(
                    filtered,
                    manufacturerName,
                    data!.generatedAt,
                    riskLabel(selectedRiskLevel)
                  )
                }
              >
                PDF
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Pricing sources */}
      {data && (
        <Paper
          variant="outlined"
          sx={{
            px: 2,
            py: 1,
            mb: 2,
            bgcolor: data.priceVersionId ? '#e8f5e9' : '#fff3e0',
            borderColor: data.priceVersionId ? '#c8e6c9' : '#ffe0b2',
          }}
        >
          {data.priceVersionId ? (
            <Typography variant="body2" color="success.dark">
              Costs calculated from{' '}
              <strong>Published Price Version {data.priceVersionNumber}</strong>
              {data.priceVersionEffectiveDate
                ? ` (Effective: ${DateUtils.parseDate(data.priceVersionEffectiveDate).toFormat('MMM d, yyyy')})`
                : ''}
              {data.paintDataVersionNumber != null && (
                <>
                  {' '}
                  and <strong>Paint Data Version {data.paintDataVersionNumber}</strong>
                </>
              )}
              .
              {data.fullyBurdenedLaborRate != null &&
                ` Labor: ${fmt(data.fullyBurdenedLaborRate)}/hr, ${data.productionFeetPerHour} production ft/hr.`}
            </Typography>
          ) : (
            <Typography variant="body2" color="warning.dark">
              No published Price Version: costs cannot be calculated until prices are published.
            </Typography>
          )}
        </Paper>
      )}

      {data && data.recordsMissingPrice > 0 && data.priceVersionId && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>
            {data.recordsMissingPrice} BOM record{data.recordsMissingPrice === 1 ? '' : 's'}
          </strong>{' '}
          reference a material with no price in the published Price Version. Trusses using them
          cannot be priced until the price is added.
        </Alert>
      )}

      <RiskToggleBar
        riskLevels={riskLevels}
        effectiveRiskLevel={selectedRiskLevel}
        onSelect={setSelectedRiskLevel}
      />

      {data && !riskLevels.includes('II') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Risk II BOM data is loaded for this manufacturer. Permanent Dwelling trusses cannot be
          priced. Contact Support.
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load BOM specifications. Please try again.
        </Alert>
      )}

      {data && !isLoading && records.length > 0 && (
        <>
          {/* Product type tabs, wind filter and search */}
          <Paper
            variant="outlined"
            sx={{ mb: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, gap: 1 }}>
              <Tabs
                value={productType}
                onChange={(_, val) => setProductType(val)}
                sx={{
                  flex: 1,
                  minHeight: 48,
                  '& .MuiTab-root': { minHeight: 48, fontSize: '0.8125rem', textTransform: 'none' },
                }}
              >
                <Tab label="All" value="All" />
                {BOM_PRODUCT_TYPES.map(type => (
                  <Tab key={type} label={type} value={type} />
                ))}
              </Tabs>
              <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
              <Select
                size="small"
                value={windSpeedFrom}
                onChange={e => setWindSpeedFrom(e.target.value as number | 'all')}
                sx={{ fontSize: '0.8125rem', my: 1, minWidth: 160 }}
                inputProps={{ 'aria-label': 'Wind speed range' }}
              >
                <MenuItem value="all">All wind speeds</MenuItem>
                {ranges.map(range => (
                  <MenuItem key={range.from} value={range.from}>
                    {windRangeLabel({ windSpeedFrom: range.from, windSpeedTo: range.to })}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                placeholder="Search code..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ width: 200, my: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, overflow: 'hidden' }}
          >
            {groups.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No BOM records match the current filter.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      {COLUMNS.map(label => (
                        <TableCell
                          key={label}
                          align={LEFT_COLUMNS.includes(label) ? 'left' : 'right'}
                          sx={{ fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map(group => (
                      <React.Fragment key={group.key}>
                        {/* Group header row */}
                        <TableRow>
                          <TableCell
                            colSpan={COLUMNS.length}
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.8125rem',
                              py: 0.75,
                              px: 1.5,
                            }}
                          >
                            {group.label}
                            <Chip
                              label={riskLabel(selectedRiskLevel)}
                              size="small"
                              sx={{ ml: 1, height: 18, fontSize: '0.7rem', bgcolor: '#e3f2fd' }}
                            />
                            <Chip
                              label={windRangeLabel(group)}
                              size="small"
                              sx={{ ml: 1, height: 18, fontSize: '0.7rem', bgcolor: '#fff3e0' }}
                            />
                          </TableCell>
                        </TableRow>

                        {group.records.map(record => {
                          const isOpen = expanded.has(record.id);
                          const missing = !record.costs.isPriced;
                          return (
                            <React.Fragment key={record.id}>
                              <TableRow
                                hover
                                onClick={() => toggleExpanded(record.id)}
                                sx={{
                                  cursor: 'pointer',
                                  bgcolor: isOpen ? 'action.selected' : undefined,
                                }}
                              >
                                <TableCell sx={{ ...cellSx, color: 'primary.main' }}>
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                                  >
                                    {isOpen ? (
                                      <ExpandedIcon fontSize="small" />
                                    ) : (
                                      <CollapsedIcon fontSize="small" />
                                    )}
                                    {record.trussCode}
                                  </Box>
                                  {missing && (
                                    <Chip
                                      label="Missing Price"
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  {fmtRange(record.spanFrom, record.spanTo)}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  {record.windSpeedFrom}–{record.windSpeedTo}
                                </TableCell>
                                <TableCell align="right" sx={cellSx}>
                                  {missing ? '—' : fmt(record.costs.steelCost)}
                                </TableCell>
                                <TableCell align="right" sx={cellSx}>
                                  {missing ? '—' : fmt(record.costs.hardwareCost)}
                                </TableCell>
                                <TableCell align="right" sx={cellSx}>
                                  {fmt(record.costs.paintCost)}
                                </TableCell>
                                <TableCell align="right" sx={cellSx}>
                                  {record.costs.laborHours.toFixed(1)}
                                </TableCell>
                                <TableCell align="right" sx={cellSx}>
                                  {fmt(record.costs.laborCost)}
                                </TableCell>
                                <TableCell align="right" sx={{ ...cellSx, fontWeight: 600 }}>
                                  {missing ? '—' : fmt(record.costs.totalCost)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ ...cellSx, fontWeight: 600, color: 'primary.main' }}
                                >
                                  {record.msrp != null ? fmt(record.msrp) : '—'}
                                </TableCell>
                              </TableRow>
                              {isOpen && (
                                <TableRow>
                                  <TableCell colSpan={COLUMNS.length} sx={{ p: 0 }}>
                                    <BOMRecordDetail
                                      record={record}
                                      productionFeetPerHour={data.productionFeetPerHour}
                                      fullyBurdenedLaborRate={data.fullyBurdenedLaborRate}
                                      paintCostPerLinearFoot={data.paintCostPerLinearFoot}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Footer */}
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: 'grey.50',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Showing {filtered.length} of {records.length} records (
                {riskLabel(selectedRiskLevel)})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click a row to see its materials and cost calculation
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {data && !isLoading && records.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No BOM data is loaded for this manufacturer. Contact Support.
        </Alert>
      )}
    </Box>
  );
};

export default BOMSpecificationsPage;
